import { useRef, useState } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import AttachFileIcon from '@assets/crm/AttachFile.svg'
import DownloadIcon from '@assets/crm/Download.svg'
import AddIcon from '@assets/crm/Add.svg'
import PersonIcon from '@assets/crm/Person.svg'
import type { APICrmFile } from '@/services/crmService'
import { formatCrmDate } from '@/utils/crm'
import { useAppTheme } from '@/theme/ThemeContext'

const FileAreaTaskCreator = ({
  files,
  canUpload,
  uploading = false,
  onUpload,
  onDownload,
  onDownloadAll,
}: {
  files: APICrmFile[]
  /** Files attach to a saved task, so uploads are only possible in edit mode. */
  canUpload: boolean
  uploading?: boolean
  onUpload: (file: File) => void | Promise<void>
  onDownload: (file: APICrmFile) => void
  onDownloadAll: () => void
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const TEXT_COLOR = isDarkMode ? '#FFFFFF' : '#003F61'
  const BORDER_COLOR = isDarkMode ? '#374151' : '#E4E7EB'
  const FILE_BG = isDarkMode ? '#091327' : '#E3F5FF'
  const iconFilter = isDarkMode ? 'brightness(0) invert(1)' : 'none'

  // One file at a time: each upload answers with the task's whole file list.
  const uploadAll = async (list: FileList | File[]) => {
    for (const file of Array.from(list)) await onUpload(file)
  }

  return (
    <Box
      data-testid="crm-file-drop"
      onDragOver={e => {
        if (!e.dataTransfer.types.includes('Files')) return
        // Without this the browser opens a dropped file instead of handing it over.
        e.preventDefault()
        if (canUpload) setDragOver(true)
      }}
      onDragLeave={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null))
          setDragOver(false)
      }}
      onDrop={e => {
        e.preventDefault()
        setDragOver(false)
        if (canUpload && !uploading) void uploadAll(e.dataTransfer.files)
      }}
      sx={{
        marginTop: '30px',
        borderRadius: '4.5px',
        outline: dragOver ? '2px dashed #72A4EB' : 'none',
        outlineOffset: '4px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${BORDER_COLOR}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip
            title={
              canUpload ? 'צירוף קובץ' : 'ניתן לצרף קבצים לאחר שמירת המשימה'
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={!canUpload || uploading}
                onClick={() => inputRef.current?.click()}
              >
                {uploading ? (
                  <CircularProgress size={16} />
                ) : (
                  <img
                    src={AddIcon}
                    alt="AddIcon"
                    style={{ opacity: canUpload ? 1 : 0.4, filter: iconFilter }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="הורדת כל הקבצים">
            <span>
              <IconButton
                size="small"
                disabled={files.length === 0}
                onClick={onDownloadAll}
              >
                <img
                  src={DownloadIcon}
                  alt="DownloadIcon"
                  style={{
                    opacity: files.length ? 1 : 0.4,
                    filter: iconFilter,
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <input
            ref={inputRef}
            type="file"
            hidden
            onChange={e => {
              const file = e.target.files?.[0]
              // Reset so picking the same file again still fires onChange.
              e.target.value = ''
              if (file) onUpload(file)
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '100%',
              color: TEXT_COLOR,
            }}
          >
            קבצים מצורפים
          </Typography>
          <img
            src={AttachFileIcon}
            alt="AttachFileIcon"
            style={{ filter: iconFilter }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          maxHeight: '150px',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#F1F5F9',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#CBD5E1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#94A3B8',
          },
        }}
      >
        {files.map(file => (
          <Box
            key={file.fileId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
              borderRadius: '4.5px',
              background: FILE_BG,
              padding: '12px 13px',
              marginTop: '15px',
            }}
          >
            <IconButton
              size="small"
              onClick={() => onDownload(file)}
              aria-label="הורדה"
            >
              <img
                src={DownloadIcon}
                alt="DownloadIcon"
                style={{ filter: iconFilter }}
              />
            </IconButton>
            <Box>
              <Typography
                sx={{
                  color: TEXT_COLOR,
                  textAlign: 'right',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 300,
                  lineHeight: '100%',
                  wordBreak: 'break-all',
                }}
              >
                {file.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    color: '#8F98A5',
                    textAlign: 'center',
                    fontFamily: 'Rubik, sans-serif',
                    fontSize: '10px',
                    fontStyle: 'normal',
                    fontWeight: 300,
                    lineHeight: '120%',
                  }}
                >
                  {formatCrmDate(file.uploadedAt)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Typography
                    sx={{
                      color: '#8F98A5',
                      textAlign: 'center',
                      fontFamily: 'Rubik, sans-serif',
                      fontSize: '11px',
                      fontStyle: 'normal',
                      fontWeight: 300,
                      lineHeight: '120%',
                    }}
                  >
                    {file.uploadedByName || '-'}
                  </Typography>
                  <img src={PersonIcon} alt="PersonIcon" />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default FileAreaTaskCreator
